import axios from 'axios';

// Google Sheets document ID from the URL
const SHEET_ID = '1doyo2LkQYEvtls-JM4ohZBmPqU-v695gPri_0H0Xb9o';
const SHEET_NAME = 'Sheet1';

// Convert Google Sheets to JSON endpoint
// This uses the public CSV export feature and converts it to JSON
const SHEETS_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${SHEET_NAME}`;

// Alternative: Direct CSV export (if JSON doesn't work)
const CSV_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv`;

export const fetchBoatInventoryFromSheets = async () => {
  console.log('📊 Fetching boat inventory from Google Sheets...');
  console.log('📍 Sheet ID:', SHEET_ID);
  
  try {
    // Use our backend server to fetch Google Sheets data (avoids CORS)
    // Using full URL as fallback if proxy doesn't work
    const apiUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:5001/api/sheets/boats'
      : '/api/sheets/boats';
    
    console.log('🔄 Fetching from:', apiUrl);
    
    const response = await axios.get(apiUrl);
    
    console.log('✅ Google Sheets data received!');
    console.log('📊 Response:', response.data);
    console.log(`🚤 Got ${response.data?.boats?.length || 0} boats from spreadsheet`);
    
    // Server already returns data in the correct format
    return response.data;
    
  } catch (error) {
    console.error('❌ Error fetching from Google Sheets:', error);
    
    // Try CSV as fallback
    console.log('🔄 Trying CSV export as fallback...');
    try {
      const csvResponse = await axios.get(CSV_URL);
      console.log('✅ CSV data received!');
      
      const boats = parseCSV(csvResponse.data);
      console.log(`🚤 Parsed ${boats.length} boats from CSV`);
      
      return {
        manufacturerId: "aa4bfc6e-c108-4bbd-ae5d-f0072ac51641",
        boats: boats
      };
    } catch (csvError) {
      console.error('❌ CSV fetch also failed:', csvError);
      throw csvError;
    }
  }
};

// Transform Google Sheets JSON data to our boat format
function transformSheetsData(data) {
  const boats = [];
  
  try {
    // Google Sheets data structure
    const rows = data.table.rows;
    const cols = data.table.cols;
    
    console.log(`📋 Processing ${rows.length} rows from spreadsheet`);
    
    // Skip header row (index 0) and process data rows
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row.c || row.c.length < 8) continue; // Skip incomplete rows
      
      // Extract cell values (v = value)
      const id = row.c[0]?.v;
      const title = row.c[1]?.v;
      const manufacturer = row.c[2]?.v;
      const model = row.c[3]?.v;
      const year = row.c[4]?.v;
      const price = row.c[5]?.v;
      const imageUrlsRaw = row.c[6]?.v;
      const lengthFeet = row.c[12]?.v;
      
      // Skip empty rows
      if (!id && !title) continue;
      
      // Parse image URLs if they exist
      let imageUrls = [];
      if (imageUrlsRaw) {
        try {
          // Remove escape characters and parse as JSON array
          const cleaned = imageUrlsRaw.replace(/\\/g, '');
          imageUrls = JSON.parse(cleaned);
        } catch (e) {
          console.warn('Failed to parse image URLs for boat', id, e);
          imageUrls = [];
        }
      }
      
      const boat = {
        id: id || Math.random().toString(36).substr(2, 9),
        title: title || `${year} ${model}`,
        manufacturer: manufacturer || 'Centurion Boats',
        model: model || '',
        year: parseInt(year) || new Date().getFullYear(),
        price: parseFloat(price) || null,
        imageUrls: imageUrls,
        lengthFeet: lengthFeet || '',
        // Generate some default values for missing fields
        locationCity: 'Available',
        locationState: 'Multiple Locations',
        hours: Math.floor(Math.random() * 500),
        description: `${title || model} - Premium Centurion boat available for purchase.`,
        stocknumber: `CENT-${id || Math.random().toString(36).substr(2, 6).toUpperCase()}`
      };
      
      boats.push(boat);
    }
  } catch (error) {
    console.error('Error transforming sheets data:', error);
  }
  
  return boats;
}

// Parse CSV data as fallback
function parseCSV(csvText) {
  const boats = [];
  const lines = csvText.split('\n');
  
  console.log(`📋 Processing ${lines.length} lines from CSV`);
  
  // Skip header line
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    
    // Basic CSV parsing (handle commas in quotes)
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    // Map CSV columns to boat object
    const [id, title, manufacturer, model, year, price, imageUrlsRaw, , , , , , lengthFeet] = values;
    
    if (!id && !title) continue;
    
    let imageUrls = [];
    if (imageUrlsRaw) {
      try {
        // Clean and parse image URLs
        const cleaned = imageUrlsRaw.replace(/^"|"$/g, '').replace(/\\/g, '');
        imageUrls = JSON.parse(cleaned);
      } catch (e) {
        imageUrls = [];
      }
    }
    
    boats.push({
      id: id || Math.random().toString(36).substr(2, 9),
      title: title || `${year} ${model}`,
      manufacturer: manufacturer || 'Centurion Boats',
      model: model || '',
      year: parseInt(year) || new Date().getFullYear(),
      price: parseFloat(price) || null,
      imageUrls: imageUrls,
      lengthFeet: lengthFeet || '',
      locationCity: 'Available',
      locationState: 'Multiple Locations',
      hours: Math.floor(Math.random() * 500),
      description: `${title || model} - Premium Centurion boat available for purchase.`,
      stocknumber: `CENT-${id || Math.random().toString(36).substr(2, 6).toUpperCase()}`
    });
  }
  
  return boats;
}

// Export a test function to check if the sheet is accessible
export const testSheetsAccess = async () => {
  try {
    console.log('🧪 Testing Google Sheets access...');
    console.log('📍 Sheet URL:', `https://docs.google.com/spreadsheets/d/${SHEET_ID}`);
    
    const response = await axios.head(CSV_URL);
    console.log('✅ Google Sheets is accessible!');
    return true;
  } catch (error) {
    console.error('❌ Cannot access Google Sheets:', error.message);
    console.log('💡 Make sure the sheet is publicly accessible:');
    console.log('   1. Open the Google Sheet');
    console.log('   2. Click Share button');
    console.log('   3. Change to "Anyone with the link can view"');
    return false;
  }
};
